import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit2, ArrowLeft } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { homeworkService } from '../services/homeworkService';
import { classService } from '../services/classService';
import { useSelector } from 'react-redux';

const HomeworkView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [homework, setHomework] = useState(null);
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch homework details
        const homeworkData = await homeworkService.getHomeworkById(id, user.token);
        if (homeworkData) {
          setHomework(homeworkData);
          
          // Fetch class details using the classId from homework
          if (homeworkData.classId) {
            // We need to fetch class details to get students and incharge
            // Assuming classService has a method to get class by ID which returns populated data
            // Or we can use the class data returned within homework if it's populated
            
            // Based on Homework model, getHomeworkById populates 'class' field.
            // But let's see if it populates students. 
            // The Homework model lookup for class does NOT seem to populate enrolledStudents deeply or at all if not specified.
            // Let's check if homeworkData.class contains enrolledStudents.
            
            // If not, we might need to fetch class details separately.
            // Let's try fetching class details explicitly to be sure we get students.
            // The classService.getClassById might be what we need if it calls the API.
            // But classService.getClassById in the file I viewed earlier was synchronous and used a local array.
            // I should use the API directly or update classService.
            
            // Let's use the APIRequest pattern seen in ClassView.js for fetching class data
            // actually ClassView uses: const response = await api.request(`/admin/getClass?id=${id}`, 'GET', {}, user.token);
            
            // Let's do that here too.
            const { classService } = require('../services/classService'); // Re-import to be safe or just use APIRequest
            // Actually, let's just use the class ID from homework to fetch full class details
             // We can't use classService.getClassById if it's not async/API based.
             // Let's look at ClassView again. It uses APIRequest directly.
          }
        } else {
          error('Homework not found');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user.token, error]);

  // Fetch class details separately when homework is loaded
  useEffect(() => {
    const fetchClassDetails = async () => {
      if (!homework?.classId) return;

      try {
        // We need to import APIRequest
        const APIRequest = require('../api/APIRequest').default;
        const api = new APIRequest();
        const response = await api.request(`/admin/getClass?id=${homework.classId}`, 'GET', {}, user.token);
        
        if (response.success) {
          setClassData(response.class);
        }
      } catch (err) {
        console.error('Error fetching class details:', err);
      }
    };

    if (homework) {
      fetchClassDetails();
    }
  }, [homework, user.token]);

  const handleStatusUpdate = async (studentId, status) => {
    try {
      const response = await homeworkService.updateHomeworkStatus(id, studentId, status, user.token);
      if (response.success) {
        // success('Status updated successfully');
        // Update local state
        setHomework(prev => {
           const newStatus = { studentId, status, completedAt: status === 'completed' ? Date.now() : null };
           const existingStatusIndex = prev.studentStatus?.findIndex(s => s.studentId === studentId);
           
           let updatedStudentStatus = prev.studentStatus ? [...prev.studentStatus] : [];
           if (existingStatusIndex !== undefined && existingStatusIndex >= 0) {
               updatedStudentStatus[existingStatusIndex] = newStatus;
           } else {
               updatedStudentStatus.push(newStatus);
           }
           
           return { ...prev, studentStatus: updatedStudentStatus };
        });
      } else {
        error(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      error('Failed to update status');
    }
  };

  const getStudentStatus = (studentId) => {
    return homework?.studentStatus?.find(s => s.studentId === studentId)?.status || 'pending';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!homework) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Homework Not Found</h2>
          <button
            onClick={() => navigate('/homeworks')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Homeworks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between animate-slideInDown">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/homeworks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {homework.name}
            </h1>
            <p className="text-gray-600 mt-1">Homework Details</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Homework Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment Details</h3>
          <div className="space-y-4">
            <div>
              <span className="text-gray-600 block text-sm">Description</span>
              <p className="font-medium mt-1">{homework.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-600 block text-sm">Due Date</span>
                <p className="font-medium mt-1 text-red-600">
                  {new Date(homework.dueDate).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-gray-600 block text-sm">Assigned Date</span>
                <p className="font-medium mt-1">
                  {new Date(homework.assignedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div>
              <span className="text-gray-600 block text-sm">Academic Year</span>
              <p className="font-medium mt-1">{homework.academicYear}</p>
            </div>
            {homework.staff && (
              <div>
                <span className="text-gray-600 block text-sm">Assigned By</span>
                <p className="font-medium mt-1 text-blue-600">{homework.staff.name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Class Incharge */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Class Incharge</h3>
          {classData?.classIncharge ? (
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center overflow-hidden">
                {classData.classIncharge.image ? (
                  <img 
                    src={`/${classData.classIncharge.image}`} 
                    alt={classData.classIncharge.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-full bg-emerald-100 flex items-center justify-center ${classData.classIncharge.image ? 'hidden' : ''}`}>
                  <span className="text-emerald-600 font-semibold">
                    {classData.classIncharge.name?.split(' ').map(n => n?.[0]).join('').slice(0, 2)}
                  </span>
                </div>
              </div>
              <div>
                <p className="font-medium">{classData.classIncharge.name}</p>
                <p className="text-sm text-gray-600">{classData.classIncharge.email}</p>
                <p className="text-xs text-gray-500">{classData.classIncharge.qualification}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No incharge assigned</p>
          )}
        </div>

        {/* Class Info */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Class Information</h3>
          {classData ? (
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Class:</span>
                <p className="font-medium">{classData.name} - {classData.section}</p>
              </div>
              <div>
                <span className="text-gray-600">Total Students:</span>
                <p className="font-medium">{classData.enrolledStudents?.length || 0}</p>
              </div>
              <div>
                <span className="text-gray-600">Completed:</span>
                <p className="font-medium text-green-600">
                  {homework.studentStatus?.filter(s => s.status === 'completed').length || 0} / {classData.enrolledStudents?.length || 0}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Loading class info...</p>
          )}
        </div>
      </div>

      {/* Enrolled Students List */}
      <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Assigned Students</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classData?.enrolledStudents?.length > 0 ? (
            classData.enrolledStudents.map(student => {
              const status = getStudentStatus(student._id || student.id);
              return (
                <div key={student._id || student.id} className={`flex flex-col p-3 bg-gray-50 rounded-lg border ${status === 'completed' ? 'border-green-200 bg-green-50' : 'border-gray-100'}`}>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                      {student.image ? (
                        <img 
                          src={`/${student.image}`} 
                          alt={student.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-blue-100 flex items-center justify-center ${student.image ? 'hidden' : ''}`}>
                        <span className="text-blue-600 font-semibold text-sm">
                          {student.name?.split(' ').map(n => n?.[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">{student.name}</p>
                      <p className="text-xs text-gray-500">{student.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 mt-auto">
                    <button
                      onClick={() => handleStatusUpdate(student._id || student.id, 'completed')}
                      className={`flex-1 py-1 px-2 text-xs rounded transition-colors ${
                        status === 'completed' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-green-50'
                      }`}
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(student._id || student.id, 'pending')}
                      className={`flex-1 py-1 px-2 text-xs rounded transition-colors ${
                        status === 'pending' 
                          ? 'bg-yellow-500 text-white' 
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-yellow-50'
                      }`}
                    >
                      Pending
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              No students found in this class.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeworkView;
